import { ArrowDropDown } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    List,
    ListItem,
    Typography,
    alpha,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import {
    getCatalogItemsAreLoading,
    getNewPropertyCatalogItemsTotalByCategoryMap,
    getSelectedMarketplaceItemsTotalByCategoryMap,
    getSelectedMarketplaceItemsTotalBySubCategoryMap,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { Category, isUncategorizedCatalogCategory } from 'entities/Category';
import {
    getSelectCategoryId,
    getSelectSubCategoryId,
} from 'entities/Category/model/selectors/categorySelectors';
import { categoryActions } from 'entities/Category/model/slices/CategorySlice';
import { GlobalCategory } from 'entities/Category/model/types/CategorySchema';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { CategoryContextMenu } from 'features/CategoryContextMenu';
import { CategoryForm } from 'features/CategoryForm';
import { CategoryFormMode } from 'features/CategoryForm/module/types/categoryFormsTypes';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import {
    getRouteGlobalCatalog,
    getRouteOrderMarketplaceCategory,
    getRoutePropertiesCatalog,
    getRoutePropertiesCatalogInventory,
    getRoutePropertiesCatalogInventoryCategoriesSelected,
    getRoutePropertiesCatalogSelected,
    getRoutePropertiesGlobalCatalogSelected,
} from 'shared/const/router';
import { useIsOrdersAddItemsMarketplace } from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { Loader } from 'shared/ui/Loader/Loader';
import { TruncatedText } from 'shared/ui/TruncatedText/TruncatedText';
import { fetchSubCategories } from 'widgets/PropertyCatalog/models/services/fetchSubCategories/fetchSubCategories';

interface PropertyCatalogCategoryProps {
    category: Category;
    editable?: boolean;
}

const SubcategoryDropdownArrow = ({
    loading,
    onExpand,
}: {
    loading: boolean;
    onExpand: () => void;
}) => {
    return loading ? (
        <Loader />
    ) : (
        <ArrowDropDown
            onClick={onExpand}
            sx={(theme) => ({
                color: alpha(theme.palette.common?.black, 0.3),
            })}
        />
    );
};

const getCategoryCount = (
    categoryId: string,
    {
        isGlobalCatalog,
        isInventoryRoute,
        marketplaceCountsByCategory,
        propertyCatalogCountsByCategory,
    }: {
        isGlobalCatalog: boolean;
        isInventoryRoute: boolean;
        marketplaceCountsByCategory: Record<string, number>;
        propertyCatalogCountsByCategory: Record<string, number>;
    },
) => {
    if (isGlobalCatalog) {
        return marketplaceCountsByCategory[categoryId] ?? 0;
    }
    if (isInventoryRoute) {
        return 0;
    }
    return propertyCatalogCountsByCategory[categoryId] ?? 0;
};

export const PropertyCatalogCategory = ({
    category,
    editable,
}: PropertyCatalogCategoryProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const itemAreLoading = useSelector(getCatalogItemsAreLoading);
    const selectedCategoryId = useSelector(getSelectCategoryId);
    const selectedSubCategoryId = useSelector(getSelectSubCategoryId);
    const propertyCatalogCountsByCategory = useSelector(
        getNewPropertyCatalogItemsTotalByCategoryMap,
    );
    const marketplaceCountsByCategory = useSelector(
        getSelectedMarketplaceItemsTotalByCategoryMap,
    );
    const marketplaceCountsBySubCategory = useSelector(
        getSelectedMarketplaceItemsTotalBySubCategoryMap,
    );
    const catalogId = useSelector(getSelectedPropertyCatalogId);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [subCategories, setSubCategories] = useState<GlobalCategory[]>([]);
    const [loading, setLoading] = useState(false);

    const startEditing = useCallback(() => {
        setIsEditing(true);
    }, []);

    const stopEditing = useCallback(() => {
        setIsEditing(false);
    }, []);

    const isInventory = useMatch(
        getRoutePropertiesCatalogInventory(':propertyId') + '/*',
    );

    const isGlobalCatalog = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );

    const isOrderGlobalCatalog = useIsOrdersAddItemsMarketplace();

    const isGlobal = useMemo(
        () => !!isOrderGlobalCatalog || !!isGlobalCatalog,
        [isOrderGlobalCatalog, isGlobalCatalog],
    );

    const isCatalog = useMatch(getRoutePropertiesCatalog(':propertyId') + '/*');
    const clearItemTextSearch = useCallback(() => {
        dispatch(searchCatalogItemsActions.setGlobalCatItemSearchValue(''));
    }, [dispatch]);

    const mainCategoryIsActive =
        category.id === selectedCategoryId && !selectedSubCategoryId;

    const categoryCount = getCategoryCount(category.id, {
        isGlobalCatalog: isGlobal,
        isInventoryRoute: Boolean(isInventory),
        marketplaceCountsByCategory,
        propertyCatalogCountsByCategory,
    });

    useEffect(
        () => () => {
            dispatch(categoryActions.clearCategorySelection());
        },
        [dispatch],
    );

    const onSelect = useCallback(
        (category: Category) => () => {
            if (!itemAreLoading) {
                dispatch(categoryActions.selectCategory(category));
                dispatch(categoryActions.selectSubCategory(null));

                if (isInventory) {
                    navigate(
                        getRoutePropertiesCatalogInventoryCategoriesSelected(
                            selectedPropertyId,
                            category.id,
                        ),
                    );
                } else if (isOrderGlobalCatalog) {
                    navigate(getRouteOrderMarketplaceCategory(category.id), {
                        state: location.state,
                    });
                    clearItemTextSearch();
                } else if (isGlobalCatalog) {
                    navigate(
                        getRoutePropertiesGlobalCatalogSelected(
                            selectedPropertyId,
                            category.id,
                        ),
                    );
                    clearItemTextSearch();
                } else if (isCatalog) {
                    navigate(
                        getRoutePropertiesCatalogSelected(
                            selectedPropertyId,
                            category.id,
                        ),
                    );
                }
            }
        },
        [
            itemAreLoading,
            dispatch,
            isInventory,
            isGlobalCatalog,
            isOrderGlobalCatalog,
            isCatalog,
            navigate,
            selectedPropertyId,
            clearItemTextSearch,
            location.state,
        ],
    );

    const onExpand = async () => {
        if (loading) return;

        setLoading(true);
        if (!isExpanded) {
            const subcategories = await fetchSubCategories(category.id);
            setSubCategories(subcategories || []);
        }

        setIsExpanded((prevState) => !prevState);

        setLoading(false);
    };

    const onSelectSubCategory = (globalCategory: GlobalCategory) => () => {
        dispatch(categoryActions.selectSubCategory(globalCategory));
        dispatch(categoryActions.selectCategory(category));
        clearItemTextSearch();

        if (isOrderGlobalCatalog) {
            navigate(getRouteOrderMarketplaceCategory(category.id), {
                state: location.state,
            });
        } else if (isGlobalCatalog) {
            navigate(
                getRoutePropertiesGlobalCatalogSelected(
                    selectedPropertyId,
                    category.id,
                ),
            );
        }
    };

    return (
        <Box>
            <Accordion
                expanded={isExpanded}
                sx={{
                    bgcolor: mainCategoryIsActive ? 'grey.300' : undefined,
                }}
            >
                <AccordionSummary
                    expandIcon={
                        isGlobal ? (
                            <SubcategoryDropdownArrow
                                onExpand={onExpand}
                                loading={loading}
                            />
                        ) : undefined
                    }
                    sx={{
                        borderLeft: mainCategoryIsActive && '2px solid',
                        borderColor: mainCategoryIsActive && 'primary.main',
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        flex="1"
                        onClick={isGlobal ? onExpand : onSelect(category)}
                        sx={{
                            '&:hover .context-menu-icon, & .context-menu-icon.is-open':
                                {
                                    opacity: 1,
                                },
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            flex="1 1"
                        >
                            <Box display="flex">
                                <Box display="flex">
                                    <Box display="flex" alignItems="center">
                                        {category?.color &&
                                            !isUncategorizedCatalogCategory(
                                                category,
                                            ) && (
                                                <Box
                                                    width="8px"
                                                    height="8px"
                                                    borderRadius="50%"
                                                    mr="8px"
                                                    sx={{
                                                        background:
                                                            category.color,
                                                    }}
                                                />
                                            )}

                                        <TruncatedText width="300px">
                                            {category.title}
                                        </TruncatedText>
                                        {categoryCount > 0 && (
                                            <Typography
                                                component="span"
                                                marginLeft="6px"
                                            >
                                                ({categoryCount})
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography
                                        color={(theme) =>
                                            alpha(
                                                theme.palette.common?.black,
                                                0.5,
                                            )
                                        }
                                    >
                                        {category.glCode
                                            ? ` | ${category.glCode}`
                                            : ''}
                                    </Typography>
                                </Box>
                            </Box>
                            {editable && (
                                <CategoryContextMenu onEdit={startEditing} />
                            )}
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <List sx={{ p: 0 }}>
                        {subCategories.map((subCat) => {
                            const isActive =
                                subCat.uid === selectedSubCategoryId;
                            const subCategoryCount =
                                marketplaceCountsBySubCategory[subCat.uid] || 0;
                            const displayText =
                                subCategoryCount > 0
                                    ? `${subCat.catName} (${subCategoryCount})`
                                    : subCat.catName;
                            return (
                                <ListItem
                                    data-testid="subcategories-name"
                                    sx={{
                                        pl: '16px',
                                        pr: '40px',
                                        borderTop: '1px solid',
                                        borderTopColor: 'grey.500',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                        borderLeft: isActive && '2px solid',
                                        borderLeftColor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                    onClick={onSelectSubCategory(subCat)}
                                    key={subCat.catName}
                                >
                                    <Typography
                                        component="div"
                                        sx={(theme) => ({
                                            color: !isActive
                                                ? alpha(
                                                      theme.palette.text
                                                          .primary,
                                                      0.6,
                                                  )
                                                : undefined,
                                        })}
                                    >
                                        <TruncatedText width="400px">
                                            {displayText}
                                        </TruncatedText>
                                    </Typography>
                                </ListItem>
                            );
                        })}
                    </List>
                </AccordionDetails>
            </Accordion>
            {isEditing && (
                <CategoryForm
                    mode={CategoryFormMode.EDIT}
                    catalogId={catalogId}
                    glCode={category.glCode}
                    categoryId={category.id}
                    title={category.title}
                    color={category.color}
                    onClose={stopEditing}
                    formTitle={t('Edit Category')}
                />
            )}
        </Box>
    );
};
